import { EventEmitter } from 'events';
import { logger } from '../../utils/logger';
import { Context, ContextItem, ContextType, ToolContext } from './types';
import { ContextBuilder } from './context-builder';

// 工具定义
export interface ToolDefinition {
  name: string;
  description: string;
  parameters: {
    type: 'object';
    properties: Record<string, any>;
    required?: string[];
  };
  handler: (params: any) => Promise<any>;
  category?: string;
  cost?: number;              // 执行成本（0-100）
  reliability?: number;       // 可靠性（0-1）
  timeout?: number;           // 超时时间（毫秒）
}

// 工具调用请求
export interface ToolCallRequest {
  toolName: string;
  parameters: any;
  context?: Context;
  priority?: number;
  timeout?: number;
}

// 工具调用结果
export interface ToolCallResult {
  success: boolean;
  toolName: string;
  parameters: any;
  result?: any;
  error?: Error;
  duration: number;
  timestamp: number;
  metadata?: Record<string, any>;
}

// 工具选择策略
export interface ToolSelectionStrategy {
  name: string;
  select: (
    tools: ToolDefinition[],
    context: Context,
    task: any
  ) => Promise<ToolDefinition[]>;
}

// 动态工具调用器
export class DynamicToolCaller extends EventEmitter {
  private tools = new Map<string, ToolDefinition>();
  private strategies = new Map<string, ToolSelectionStrategy>();
  private callHistory: ToolCallResult[] = [];
  private concurrentCalls = 0;
  
  constructor(
    private contextBuilder: ContextBuilder,
    private config: {
      maxConcurrentCalls?: number;
      defaultTimeout?: number;
      maxRetries?: number;
      retryDelay?: number;
      enableParallel?: boolean;
      enableCache?: boolean;
      cacheTimeout?: number;
    } = {}
  ) {
    super();
    
    this.config = {
      maxConcurrentCalls: 5,
      defaultTimeout: 30000,
      maxRetries: 3,
      retryDelay: 1000,
      enableParallel: true,
      enableCache: true,
      cacheTimeout: 5 * 60 * 1000,
      ...config
    };
    
    // 注册默认策略
    this.registerDefaultStrategies();
  }
  
  /**
   * 注册工具
   */
  registerTool(tool: ToolDefinition): void {
    if (this.tools.has(tool.name)) {
      logger.warn(`Tool "${tool.name}" already registered, overwriting`);
    }
    
    this.tools.set(tool.name, {
      cost: 10,
      reliability: 0.9,
      timeout: this.config.defaultTimeout,
      ...tool
    });
    
    logger.info(`Tool registered: ${tool.name}`);
    this.emit('toolRegistered', tool);
  }
  
  /**
   * 批量注册工具
   */
  registerTools(tools: ToolDefinition[]): void {
    for (const tool of tools) {
      this.registerTool(tool);
    }
  }
  
  /**
   * 注销工具
   */
  unregisterTool(toolName: string): void {
    if (this.tools.delete(toolName)) {
      logger.info(`Tool unregistered: ${toolName}`);
      this.emit('toolUnregistered', toolName);
    }
  }
  
  /**
   * 注册选择策略
   */
  registerStrategy(strategy: ToolSelectionStrategy): void {
    this.strategies.set(strategy.name, strategy);
    logger.info(`Strategy registered: ${strategy.name}`);
  }
  
  /**
   * 基于上下文调用工具
   */
  async callWithContext(
    context: Context,
    task: any,
    strategy: string = 'smart'
  ): Promise<ToolCallResult[]> {
    const startTime = Date.now();
    
    try {
      // 选择合适的工具
      const selectedTools = await this.selectTools(context, task, strategy);
      
      if (selectedTools.length === 0) {
        logger.warn('No suitable tools found for the task');
        return [];
      }
      
      logger.info(`Selected ${selectedTools.length} tools for execution`);
      
      // 准备工具调用
      const toolCalls = await this.prepareToolCalls(
        selectedTools,
        context,
        task
      );
      
      // 执行工具调用
      let results: ToolCallResult[];
      
      if (this.config.enableParallel && toolCalls.length > 1) {
        results = await this.callParallel(toolCalls);
      } else {
        results = await this.callSequential(toolCalls);
      }
      
      // 更新上下文
      await this.updateContext(context, results);
      
      const duration = Date.now() - startTime;
      logger.info(`Tool execution completed in ${duration}ms:`, {
        toolsExecuted: results.length,
        successful: results.filter(r => r.success).length,
        failed: results.filter(r => !r.success).length
      });
      
      return results;
      
    } catch (error) {
      logger.error('Failed to execute tools with context:', error);
      throw error;
    }
  }
  
  /**
   * 直接调用工具
   */
  async call(request: ToolCallRequest): Promise<ToolCallResult> {
    const tool = this.tools.get(request.toolName);
    
    if (!tool) {
      throw new Error(`Tool not found: ${request.toolName}`);
    }
    
    // 检查并发限制
    if (this.concurrentCalls >= this.config.maxConcurrentCalls!) {
      await this.waitForSlot();
    }
    
    this.concurrentCalls++;
    const startTime = Date.now();
    
    try {
      // 验证参数
      this.validateParameters(tool, request.parameters);
      
      // 设置超时
      const timeout = request.timeout || tool.timeout || this.config.defaultTimeout!;
      
      // 执行工具
      const result = await this.executeWithTimeout(
        tool.handler(request.parameters),
        timeout
      );
      
      const callResult: ToolCallResult = {
        success: true,
        toolName: request.toolName,
        parameters: request.parameters,
        result,
        duration: Date.now() - startTime,
        timestamp: Date.now()
      };
      
      this.callHistory.push(callResult);
      this.emit('toolCalled', callResult);
      
      return callResult;
      
    } catch (error) {
      const callResult: ToolCallResult = {
        success: false,
        toolName: request.toolName,
        parameters: request.parameters,
        error: error as Error,
        duration: Date.now() - startTime,
        timestamp: Date.now()
      };
      
      this.callHistory.push(callResult);
      this.emit('toolFailed', callResult);
      
      // 重试逻辑
      if (this.shouldRetry(error as Error)) {
        return this.retryCall(request, error as Error);
      }
      
      return callResult;
      
    } finally {
      this.concurrentCalls--;
    }
  }
  
  /**
   * 并行调用工具
   */
  private async callParallel(
    requests: ToolCallRequest[]
  ): Promise<ToolCallResult[]> {
    const promises = requests.map(request => this.call(request));
    return Promise.all(promises);
  }
  
  /**
   * 顺序调用工具
   */
  private async callSequential(
    requests: ToolCallRequest[]
  ): Promise<ToolCallResult[]> {
    const results: ToolCallResult[] = [];
    
    for (const request of requests) {
      const result = await this.call(request);
      results.push(result);
      
      // 如果失败且是关键工具，停止执行
      if (!result.success && request.priority && request.priority > 80) {
        logger.warn('Critical tool failed, stopping execution');
        break;
      }
    }
    
    return results;
  }
  
  /**
   * 选择工具
   */
  private async selectTools(
    context: Context,
    task: any,
    strategyName: string
  ): Promise<ToolDefinition[]> {
    const strategy = this.strategies.get(strategyName);
    
    if (!strategy) {
      throw new Error(`Strategy not found: ${strategyName}`);
    }
    
    const allTools = Array.from(this.tools.values());
    return strategy.select(allTools, context, task);
  }
  
  /**
   * 准备工具调用
   */
  private async prepareToolCalls(
    tools: ToolDefinition[],
    context: Context,
    task: any
  ): Promise<ToolCallRequest[]> {
    const requests: ToolCallRequest[] = [];
    
    for (const tool of tools) {
      // 从上下文推断参数
      const parameters = await this.inferParameters(tool, context, task);
      
      requests.push({
        toolName: tool.name,
        parameters,
        context,
        priority: this.calculatePriority(tool, context)
      });
    }
    
    // 按优先级排序
    requests.sort((a, b) => (b.priority || 0) - (a.priority || 0));
    
    return requests;
  }
  
  /**
   * 推断参数
   */
  private async inferParameters(
    tool: ToolDefinition,
    context: Context,
    task: any
  ): Promise<any> {
    const parameters: any = {};
    
    // 从任务中提取参数
    if (task.parameters && typeof task.parameters === 'object') {
      Object.assign(parameters, task.parameters);
    }
    
    // 从上下文中提取参数
    const contextParams = this.extractParametersFromContext(tool, context);
    Object.assign(parameters, contextParams);
    
    // 填充默认值
    for (const [key, schema] of Object.entries(tool.parameters.properties)) {
      if (!(key in parameters) && 'default' in schema) {
        parameters[key] = schema.default;
      }
    }
    
    return parameters;
  }
  
  /**
   * 从上下文提取参数
   */
  private extractParametersFromContext(
    tool: ToolDefinition,
    context: Context
  ): any {
    const parameters: any = {};
    
    // 查找相关的上下文项
    const relevantItems = context.items.filter(item => {
      // 工具结果可能包含参数
      if (item.type === ContextType.TOOL) {
        const toolContext = item.content as ToolContext;
        return toolContext.result && typeof toolContext.result === 'object';
      }
      
      // 状态信息可能包含参数
      if (item.type === ContextType.STATE) {
        return true;
      }
      
      return false;
    });
    
    // 提取参数
    for (const item of relevantItems) {
      if (item.type === ContextType.TOOL) {
        const result = (item.content as ToolContext).result;
        this.extractMatchingFields(result, tool.parameters.properties, parameters);
      } else if (item.type === ContextType.STATE) {
        this.extractMatchingFields(item.content, tool.parameters.properties, parameters);
      }
    }
    
    return parameters;
  }
  
  /**
   * 提取匹配的字段
   */
  private extractMatchingFields(
    source: any,
    schema: Record<string, any>,
    target: any
  ): void {
    if (!source || typeof source !== 'object') {
      return;
    }
    
    for (const [key, fieldSchema] of Object.entries(schema)) {
      if (key in source && !(key in target)) {
        // 验证类型匹配
        const value = source[key];
        const expectedType = fieldSchema.type;
        
        if (this.validateType(value, expectedType)) {
          target[key] = value;
        }
      }
    }
  }
  
  /**
   * 验证类型
   */
  private validateType(value: any, expectedType: string): boolean {
    switch (expectedType) {
      case 'string':
        return typeof value === 'string';
      case 'number':
        return typeof value === 'number';
      case 'boolean':
        return typeof value === 'boolean';
      case 'array':
        return Array.isArray(value);
      case 'object':
        return typeof value === 'object' && value !== null;
      default:
        return true;
    }
  }
  
  /**
   * 计算优先级
   */
  private calculatePriority(tool: ToolDefinition, context: Context): number {
    let priority = 50;
    
    // 基于工具可靠性
    priority += (tool.reliability || 0.9) * 20;
    
    // 基于工具成本（成本越低优先级越高）
    priority += (100 - (tool.cost || 10)) / 5;
    
    // 基于上下文相关性
    const relevantItems = context.items.filter(item => {
      const content = JSON.stringify(item.content).toLowerCase();
      return content.includes(tool.name.toLowerCase()) ||
             content.includes(tool.category?.toLowerCase() || '');
    });
    
    priority += Math.min(relevantItems.length * 5, 20);
    
    return Math.min(priority, 100);
  }
  
  /**
   * 验证参数
   */
  private validateParameters(tool: ToolDefinition, parameters: any): void {
    const required = tool.parameters.required || [];
    
    for (const field of required) {
      if (!(field in parameters)) {
        throw new Error(`Missing required parameter: ${field}`);
      }
    }
    
    // 可以添加更复杂的 schema 验证
  }
  
  /**
   * 带超时执行
   */
  private async executeWithTimeout<T>(
    promise: Promise<T>,
    timeout: number
  ): Promise<T> {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) => 
        setTimeout(() => reject(new Error('Tool execution timeout')), timeout)
      )
    ]);
  }
  
  /**
   * 等待执行槽位
   */
  private async waitForSlot(): Promise<void> {
    while (this.concurrentCalls >= this.config.maxConcurrentCalls!) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
  
  /**
   * 判断是否应该重试
   */
  private shouldRetry(error: Error): boolean {
    // 网络错误、超时等可以重试
    const retryableErrors = ['ETIMEDOUT', 'ECONNRESET', 'ENOTFOUND'];
    return retryableErrors.some(code => error.message.includes(code));
  }
  
  /**
   * 重试调用
   */
  private async retryCall(
    request: ToolCallRequest,
    originalError: Error,
    attempt: number = 1
  ): Promise<ToolCallResult> {
    if (attempt >= this.config.maxRetries!) {
      throw originalError;
    }
    
    logger.info(`Retrying tool call: ${request.toolName} (attempt ${attempt + 1})`);
    
    // 延迟重试
    await new Promise(resolve => 
      setTimeout(resolve, this.config.retryDelay! * attempt)
    );
    
    try {
      return await this.call(request);
    } catch (error) {
      return this.retryCall(request, error as Error, attempt + 1);
    }
  }
  
  /**
   * 更新上下文
   */
  private async updateContext(
    context: Context,
    results: ToolCallResult[]
  ): Promise<void> {
    for (const result of results) {
      const toolContext: ToolContext = {
        toolName: result.toolName,
        parameters: result.parameters,
        result: result.result,
        error: result.error,
        duration: result.duration,
        timestamp: result.timestamp
      };
      
      await this.contextBuilder.addItem(context, {
        type: ContextType.TOOL,
        content: toolContext,
        priority: 60,
        relevance: result.success ? 0.8 : 0.3,
        source: 'tool-caller',
        metadata: result.metadata
      });
    }
  }
  
  /**
   * 注册默认策略
   */
  private registerDefaultStrategies(): void {
    // 智能选择策略
    this.registerStrategy({
      name: 'smart',
      select: async (tools, context, task) => {
        // 基于任务类型和上下文选择工具
        const scores = tools.map(tool => ({
          tool,
          score: this.calculateToolScore(tool, context, task)
        }));
        
        // 按分数排序并选择前几个
        scores.sort((a, b) => b.score - a.score);
        
        const threshold = 0.5;
        return scores
          .filter(s => s.score >= threshold)
          .slice(0, 5)
          .map(s => s.tool);
      }
    });
    
    // 全部执行策略
    this.registerStrategy({
      name: 'all',
      select: async (tools) => tools
    });
    
    // 类别选择策略
    this.registerStrategy({
      name: 'byCategory',
      select: async (tools, context, task) => {
        const category = task.category || 'general';
        return tools.filter(tool => tool.category === category);
      }
    });
  }
  
  /**
   * 计算工具分数
   */
  private calculateToolScore(
    tool: ToolDefinition,
    context: Context,
    task: any
  ): number {
    let score = 0;
    
    // 名称匹配
    if (task.suggestedTools?.includes(tool.name)) {
      score += 0.5;
    }
    
    // 类别匹配
    if (tool.category === task.category) {
      score += 0.3;
    }
    
    // 描述相关性
    const taskDesc = (task.description || '').toLowerCase();
    const toolDesc = tool.description.toLowerCase();
    
    const commonWords = this.getCommonWords(taskDesc, toolDesc);
    score += Math.min(commonWords.length * 0.1, 0.3);
    
    // 可靠性加成
    score *= tool.reliability || 0.9;
    
    return score;
  }
  
  /**
   * 获取共同词汇
   */
  private getCommonWords(text1: string, text2: string): string[] {
    const words1 = new Set(text1.split(/\s+/));
    const words2 = new Set(text2.split(/\s+/));
    
    const common: string[] = [];
    for (const word of words1) {
      if (words2.has(word) && word.length > 3) {
        common.push(word);
      }
    }
    
    return common;
  }
  
  /**
   * 获取调用历史
   */
  getHistory(filter?: {
    toolName?: string;
    success?: boolean;
    limit?: number;
  }): ToolCallResult[] {
    let history = [...this.callHistory];
    
    if (filter?.toolName) {
      history = history.filter(h => h.toolName === filter.toolName);
    }
    
    if (filter?.success !== undefined) {
      history = history.filter(h => h.success === filter.success);
    }
    
    if (filter?.limit) {
      history = history.slice(-filter.limit);
    }
    
    return history;
  }
  
  /**
   * 清理历史
   */
  clearHistory(): void {
    this.callHistory = [];
  }
  
  /**
   * 获取工具统计
   */
  getStatistics(): {
    totalCalls: number;
    successRate: number;
    averageDuration: number;
    toolUsage: Map<string, number>;
  } {
    const totalCalls = this.callHistory.length;
    const successfulCalls = this.callHistory.filter(h => h.success).length;
    const totalDuration = this.callHistory.reduce((sum, h) => sum + h.duration, 0);
    
    const toolUsage = new Map<string, number>();
    for (const call of this.callHistory) {
      toolUsage.set(call.toolName, (toolUsage.get(call.toolName) || 0) + 1);
    }
    
    return {
      totalCalls,
      successRate: totalCalls > 0 ? successfulCalls / totalCalls : 0,
      averageDuration: totalCalls > 0 ? totalDuration / totalCalls : 0,
      toolUsage
    };
  }
}